import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { CreateContract, Entitate, FindEntitate, GetSlots, UpdateSlots } from './inregistrare-programare.interface';
import { BehaviorSubject } from 'rxjs';
import { StoreSubcontract, SubcontractLocal } from './disponibilitate-programare/disponibilitate.interface';
import { ContractDraft } from './initialize-programare/initialize-programare.component';
import { ContractFileGroup } from '../contracte-programari/contracte-programari.interface';

export interface DetaliiProgramareState {
    contractId: number | null;
    selectedStartDate: Date | null;
    selectedEndDate: Date | null;
    selectedLuni: number | null;
    nrContract: string;
    dataContract: string;
    subcontracteLocale: SubcontractLocal[];
    disponibilitateActiva: boolean;
    sloturiSelectateIds: { slot_id: number; date: string }[];
    fileGroups: ContractFileGroup[]; // NOU
}

@Injectable({
    providedIn: 'root'
})
export class InregistrareProgramareService {
    private basePath = environment.interop.basePath;

    private findEntitateUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.findEntitate;
    private createContractUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.createContract;
    private getSlotsUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.getSlots;
    private updateSlotsUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.updateSlots;
    private storeSubcontractUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.storeSubcontract;
    private updateSubcontractUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.updateSubcontract;
    private deleteSubcontractUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.deleteSubcontract;
    private finalizareContractUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.finalizare;
    private listContracteUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.listContracte;
    private deleteContractUrl = this.basePath + environment.interop.dgaspc.activitati.inregistrareProgramare.deleteContract;
    private getContractUrl = this.basePath + environment.interop.dgaspc.activitati.contracteProgramari.findContract;

    private selectedEntitateSubject = new BehaviorSubject<Entitate | null>(null);
    selectedEntitate$ = this.selectedEntitateSubject.asObservable();

    private entitatiSubject = new BehaviorSubject<Entitate[]>([]);

    private searchFormSubject = new BehaviorSubject<FindEntitate>({
        name: '',
        cnp: '',
        nr_dosar: '',
    });
    searchForm$ = this.searchFormSubject.asObservable();

    private detaliiStateSubject = new BehaviorSubject<DetaliiProgramareState | null>(null);
    private drafturiSubject = new BehaviorSubject<ContractDraft[]>([]);
    private activeModeSubject = new BehaviorSubject<'initializare' | 'drafturi'>('initializare');

    constructor(private http: HttpClient) { }

    findEntitate(entitate: FindEntitate) {
        return this.http.post(this.findEntitateUrl, entitate);
    }

    createContract(contract: CreateContract) {
        return this.http.post(this.createContractUrl, contract);
    }

    getSlots(payload: GetSlots) {
        return this.http.post(this.getSlotsUrl, payload);
    }

    updateSlots(payload: UpdateSlots) {
        return this.http.post(this.updateSlotsUrl, payload);
    }

    storeSubcontract(payload: StoreSubcontract) {
        return this.http.post(this.storeSubcontractUrl, payload);
    }

    updateSubcontract(subcontractId: number, payload: StoreSubcontract) {
        return this.http.patch(this.updateSubcontractUrl + subcontractId, payload);
    }

    deleteSubcontract(subcontractId: number) {
        return this.http.delete(this.deleteSubcontractUrl + subcontractId);
    }

    finalizareContact(contractId: number) {
        return this.http.patch(this.finalizareContractUrl + contractId, {});
    }

    listContracte(entitate: FindEntitate) {
        return this.http.post(this.listContracteUrl, entitate);
    }

    deleteContract(contractId: number) {
        return this.http.delete(this.deleteContractUrl + contractId);
    }

    getContract(contractId: number) {
        return this.http.get(this.getContractUrl + contractId);
    }

    // ── Entitate selectata ────────────────────────────────────────────────────

    setSelectedEntitate(entitate: Entitate) {
        const current = this.selectedEntitateSubject.getValue();
        if (current && current.id !== entitate.id) {
            this.clearDetaliiState();
        }
        this.selectedEntitateSubject.next(entitate);
    }

    setSelectedEntitateForDraft(entitate: Entitate) {
        this.selectedEntitateSubject.next(entitate);
    }

    getSelectedEntitate(): Entitate | null {
        return this.selectedEntitateSubject.getValue();
    }

    clearSelectedEntitate() {
        this.selectedEntitateSubject.next(null);
    }

    // ── Lista entitati ────────────────────────────────────────────────────────

    setEntitati(entitati: Entitate[]) {
        this.entitatiSubject.next(entitati);
    }

    getEntitati(): Entitate[] {
        return this.entitatiSubject.getValue();
    }

    // ── Form cautare ──────────────────────────────────────────────────────────

    setSearchForm(form: FindEntitate) {
        this.searchFormSubject.next(form);
    }

    getSearchForm(): FindEntitate {
        return this.searchFormSubject.getValue();
    }

    // ── Starea pasului 2 ──────────────────────────────────────────────────────

    saveDetaliiState(state: DetaliiProgramareState): void {
        this.detaliiStateSubject.next(state);
    }

    getDetaliiState(): DetaliiProgramareState | null {
        return this.detaliiStateSubject.getValue();
    }

    clearDetaliiState(): void {
        this.detaliiStateSubject.next(null);
    }

    // ── Drafturi ──────────────────────────────────────────────────────────────

    setDrafturi(drafturi: ContractDraft[]): void {
        this.drafturiSubject.next(drafturi);
    }

    getDrafturi(): ContractDraft[] {
        return this.drafturiSubject.getValue();
    }

    // ── Modul activ (tab) ─────────────────────────────────────────────────────

    setActiveMode(mode: 'initializare' | 'drafturi'): void {
        this.activeModeSubject.next(mode);
    }

    getActiveMode(): 'initializare' | 'drafturi' {
        return this.activeModeSubject.getValue();
    }

    // ── Pending edit din pasul 3 → pasul 2 ───────────────────────────────────

    private pendingEditSubcontractId: number | null = null;

    setPendingEditSubcontractId(id: number): void {
        this.pendingEditSubcontractId = id;
    }

    consumePendingEditSubcontractId(): number | null {
        const id = this.pendingEditSubcontractId;
        this.pendingEditSubcontractId = null;
        return id;
    }

    // ── Reset complet (după finalizare) ───────────────────────────────────────

    resetAll(): void {
        this.selectedEntitateSubject.next(null);
        this.entitatiSubject.next([]);
        this.searchFormSubject.next({ name: '', cnp: '', nr_dosar: '' });
        this.detaliiStateSubject.next(null);
        this.drafturiSubject.next([]);
        this.activeModeSubject.next('initializare');
    }
}